import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, PackageIcon, StarIcon } from 'lucide-react';
import { genreColors, statusColors, statusLabels } from './constants';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
interface ItemDisplayProps {
  item: any;
  darkMode: boolean;
  textPrimary: string;
  textSecondary: string;
  onClose: () => void;
  onEdit: (id: string) => void;
}
export const ItemDisplay: React.FC<ItemDisplayProps> = ({
  item,
  darkMode,
  textPrimary,
  textSecondary,
  onClose,
  onEdit
}) => {
  const rt = useRoleTheme();
  return <div className="h-full flex flex-col relative" data-id="element-17770" data-mp="16r11xk4xqxsg">
      <div className="flex-1 overflow-y-auto" data-id="element-17771" data-mp="1bqm5y8yypwc1">
        <div className="flex justify-center pt-3 pb-1 md:hidden" data-id="element-17772" data-mp="38k75p2b9ylly">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-17773" data-mp="38m1reiudehr3" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} transition-colors`} data-id="element-17774" data-mp="2xloasynm5x0w">
          <XIcon className="h-4 w-4" data-id="element-17775" data-mp="2nwwlky9xtqto" />
        </button>

        <div className="px-5 pb-6 pt-3 md:pt-5" data-id="element-17776" data-mp="2ju9ux0p06by2">
          <div className="flex gap-4 items-start" data-id="element-17777" data-mp="2jvkt8kzoirhs">
            <div className="flex-shrink-0 w-24 h-28 rounded-xl overflow-hidden shadow-lg" data-id="element-17778" data-mp="3gkaduj2oxy7g">
              <img src={item.coverImage} alt={item.name} className="w-full h-full object-cover" data-id="element-17779" data-mp="3blfwzmoaajpz" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-17780" data-mp="37lyqcpx4fgj8">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-1 ${textPrimary}`} data-id="element-17781" data-mp="32lasbkah5zfi">
                {item.name}
              </h3>
              <div className="flex flex-wrap gap-1 mb-2" data-id="element-17782" data-mp="1r5m30m9r0obj">
                <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-17783" data-mp="1r419mzz67bf9">
                  {item.theme}
                </span>
                <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium text-white ${statusColors[item.status] || 'bg-gray-500'}`} data-id="element-17784" data-mp="1jmd27hfb5xy3">
                  {statusLabels[item.status] || item.status}
                </span>
                {item.isSpecialEdition && <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-amber-900/60 text-amber-200' : 'bg-amber-100 text-amber-700'}`} data-id="element-17785" data-mp="1enidxzyknh6l">
                    Special Edition
                  </span>}
              </div>
              <div className="flex items-center gap-3" data-id="element-17786" data-mp="qx5ubrtyrw06">
                <span className={`text-lg font-bold ${textPrimary}`} data-id="element-17787" data-mp="qzijoq21x8n9">
                  ${item.price.toFixed(2)}
                </span>
                <span className={`text-xs capitalize ${textSecondary}`} data-id="element-17788" data-mp="ca76e6ubfjq7">
                  {item.subscriptionType}
                </span>
              </div>
            </div>
          </div>

          {item.description && <p className={`mt-3 text-sm ${textSecondary} line-clamp-2`} data-id="element-17789" data-mp="lc6ol2h2gtuq">
              {item.description}
            </p>}

          <div className="mt-4 flex gap-2" data-id="element-17790" data-mp="hbwk5u8xabhz">
            <button onClick={() => {
            onEdit(item.id);
            onClose();
          }} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-17791" data-mp="v8t8np1kjgvp">
              <PencilIcon className="h-4 w-4" data-id="element-17792" data-mp="101mc89lawj67" />
              Edit Box
            </button>
            <button onClick={onClose} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-17793" data-mp="1e146v5la2qnh">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export const BoxHoverCard: React.FC<{
  item: any;
  darkMode: boolean;
  mousePosition: {
    x: number;
    y: number;
  };
}> = ({
  item,
  darkMode,
  mousePosition
}) => {
  if (!item) return null;
  const showOnLeft = mousePosition.x > window.innerWidth / 2;
  const cardWidth = 260;
  return <div className={`fixed z-30 w-64 p-4 rounded-lg shadow-md border ${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'}`} style={{
    top: Math.max(mousePosition.y - 80, 20),
    left: showOnLeft ? Math.max(mousePosition.x - cardWidth - 20, 20) : Math.min(mousePosition.x + 20, window.innerWidth - cardWidth - 20)
  }} data-id="element-17794" data-mp="2phf6nz8ad0j2">
      <div className="flex gap-3" data-id="element-17795" data-mp="drcuebc1sql0">
        <div className="w-16 h-20 flex-shrink-0 rounded bg-center bg-cover" style={{
        backgroundImage: `url(${item.coverImage})`
      }} data-id="element-17796" data-mp="dqb798hrcdmx" />
        <div className="flex-1 overflow-hidden" data-id="element-17797" data-mp="ixrhfs2auu05">
          <h3 className="font-bold text-sm mb-1 line-clamp-2" data-id="element-17798" data-mp="iz2l7rvqw1r9">{item.name}</h3>
          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-17799" data-mp="e46sepiopad3">
            {item.theme}
          </p>
          <div className="flex items-center gap-2" data-id="element-17800" data-mp="55ustxzub28i">
            <span className={`text-sm font-bold`} data-id="element-17801" data-mp="538jptxaweo6">
              ${item.price.toFixed(2)}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full text-white ${statusColors[item.status] || 'bg-gray-500'}`} data-id="element-17802" data-mp="8l4yms3myms">
              {statusLabels[item.status] || item.status}
            </span>
          </div>
        </div>
      </div>
    </div>;
};
export const CoverCarousel: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
  aspectClass?: string;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave,
  aspectClass = 'aspect-[4/5]'
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [items]);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = scrollRef.current.clientWidth * 0.75;
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -amount : amount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  return <div className="relative group" data-id="element-17803" data-mp="2js5e8a9ak2a1">
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-17804" data-mp="2jr42s4od5drs">
        {items.map((item: any) => <div key={item.id} className="flex-shrink-0 w-32 sm:w-36 cursor-pointer group/item" onClick={() => onItemClick(item)} onMouseEnter={() => onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-17805" data-mp="1m7157ll990jx">
            <div className={`w-full ${aspectClass} bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover/item:scale-[1.04] group-hover/item:shadow-xl`} style={{
          backgroundImage: `url('${item.coverImage}')`
        }} title={item.name} data-id="element-17806" data-mp="18d9vac3m3eno" />
          </div>)}
      </div>
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-17807" data-mp="rs8fd4djanqb">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-17808" data-mp="15rhtlyi6j7rn" />
        </button>}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-17809" data-mp="1amdxpx0oxll4">
          <ChevronRightIcon className="h-4 w-4" data-id="element-17810" data-mp="391uyhgv6b4ag" />
        </button>}
    </div>;
};
export const ItemGrid: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
  aspectClass?: string;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave,
  aspectClass = 'aspect-[4/5]'
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-17811" data-mp="3srwz9bqw44kl">
      {items.map((item: any) => <div key={item.id} className="cursor-pointer group" onClick={() => onItemClick(item)} onMouseEnter={() => !isMobile && onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-17812" data-mp="3izr7a20n1n5e">
          <div className={`w-full ${aspectClass} bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover:scale-[1.03] group-hover:shadow-xl`} style={{
        backgroundImage: `url('${item.coverImage}')`
      }} title={item.name} data-id="element-17813" data-mp="fcs907um0nvg" />
        </div>)}
    </div>;
};
export const BoxInfoModal = ItemDisplay;